import { type ChangeEvent, Component } from 'react';
import { Button, Callout, Checkbox, HTMLTable, Intent, TextArea } from '@blueprintjs/core';
import {
  Dialog,
  Focusable,
  Heading,
  Button as LaunchpadButton,
  Modal,
  ModalOverlay,
  Tooltip,
  TooltipTrigger,
} from '@launchpad-ui/components';
import { Icon as LaunchpadIcon } from '@launchpad-ui/icons';
import { endOfYesterday, isAfter } from 'date-fns';

import { type Scopes } from '../../auth';
import { Notifications } from '../../notifications/components/Notifications';
import { type Settings } from '../../settings';
import { CircularIconButton, type RequestState } from '../../shared';
import { type Instance, LimitOverrideFlag, type LineItem, Platform, type ProcessedPlan } from '../model';
import { type ProcessedPlansByAccountIDState } from '../reducer';
import { vitalBaseUrl } from '../util';

import { PlanReviewConfig } from './PlanReviewConfig';
import { PlanReviewLineItemSection } from './PlanReviewLineItemSection';
import { PlanReviewOverrideCell } from './PlanReviewOverrideCell';
import { RequestStatus } from './RequestStatus';

import './PlanReview.scss';

type PlanReviewProps = {
  accountID: string;
  launchLineItem: LineItem | null;
  userEmail: string | null;
  absoluteMode: boolean;
  processedPlansByAccountID: ProcessedPlansByAccountIDState;
  settings: Settings;
  scopes: Scopes;
  refreshSelectedPlan: () => void;
  setLaunchOrderEndDate: (accountID: string, orderID: number, endDate: Date, absoluteMode: boolean) => void;
  setExecutedPlan: (
    accountID: string,
    inputsHash: string,
    absoluteMode: boolean,
    skipEmailNotification: boolean,
    userEmail: string | null,
  ) => void;
  setRejectedPlan: (
    accountID: string,
    inputsHash: string,
    absoluteMode: boolean,
    userEmail: string | null,
    comment: string | null,
  ) => void;
  fetchLaunchOrdersRequest: { [accountID: string]: RequestState };
  setLaunchOrderEndDateRequest: { [accountID: string]: RequestState };
  setExecutedPlanRequest: { [accountID: string]: RequestState };
  setRejectedPlanRequest: { [accountID: string]: RequestState };
};

type State = {
  isOpen: boolean;
  skipEmailNotification: boolean;
  rejectionComment: string;
  requestTitle: string | null;
  requestState: RequestState | null;
  showLoadingIndicatorOnFetch: boolean;
};

type TableHeaderProps = {
  shouldDisplayTrialDates: boolean;
  showStartingDate: boolean;
};

function TableHeader(props: TableHeaderProps) {
  return (
    <thead>
      <tr>
        <th />
        <th>Order ID</th>
        <th>Order Source</th>
        <th>Opportunity ID</th>
        <th>
          {props.showStartingDate ? 'Starting Date' : ''}{' '}
          {props.shouldDisplayTrialDates && (
            <TooltipTrigger>
              <Focusable>
                <LaunchpadIcon name="sync" size="small" role="button" tabIndex={0} className="icon-clickable" />
              </Focusable>
              <Tooltip placement="top" variant="popover">
                The start date for the single action product represents the start date of the trial extension. This
                start date will not show up in new contracted limits, which will also represent the start date of the
                plan.
              </Tooltip>
            </TooltipTrigger>
          )}
        </th>
        <th>Ending Date</th>
        <th>Platform</th>
        <th>Seats</th>
        <th>Hosts</th>
        <th>cMau</th>
        <th>Experiment Events</th>
        <th>Experimentation Keys</th>
        <th>Experimentation MAU</th>
        <th>Data Export</th>
        <th>Traces</th>
        <th>Sessions</th>
        <th>Errors</th>
        <th>Logs</th>
        <th>Analytics MAU</th>
        <th>AI Service Connections</th>
        <th>AI Runs</th>
        <th>Vega Prompts</th>
        <th>SSO</th>
      </tr>
    </thead>
  );
}

function ApproveNewLimitsButton({ plan, onExecuteClick }: { plan: ProcessedPlan; onExecuteClick: () => void }) {
  const approveButton = (
    <LaunchpadButton variant="primary" size="medium" onPress={onExecuteClick}>
      <LaunchpadIcon name="thumb-up-outline" slot="start-icon" />
      Approve New Limits
    </LaunchpadButton>
  );

  return Object.keys(plan.flagOverrides).length > 0 ? (
    <TooltipTrigger>
      {approveButton}
      <Tooltip placement="top" variant="popover">
        Approving contracted limits has no effect on Catfood overrides.
      </Tooltip>
    </TooltipTrigger>
  ) : (
    approveButton
  );
}

export class PlanReview extends Component<PlanReviewProps> {
  public state: State = {
    isOpen: false,
    skipEmailNotification: false,
    rejectionComment: '',
    requestTitle: null,
    requestState: null,
    showLoadingIndicatorOnFetch: false,
  };

  private shouldLineItemSectionBeStriped = false;

  // oxlint-disable-next-line consistent-return
  public componentDidUpdate(prevProps: Readonly<PlanReviewProps>) {
    const {
      accountID,
      fetchLaunchOrdersRequest,
      setLaunchOrderEndDateRequest,
      setExecutedPlanRequest,
      setRejectedPlanRequest,
    } = this.props;

    const requestError: boolean = this.state.requestState !== null && this.state.requestState.error !== null;

    if (!requestError && fetchLaunchOrdersRequest[accountID] !== prevProps.fetchLaunchOrdersRequest[accountID]) {
      return this.setState({
        requestTitle: 'Loading launch orders',
        requestState: fetchLaunchOrdersRequest[accountID],
        showLoadingIndicatorOnFetch: true,
      });
    }

    if (
      !requestError &&
      setLaunchOrderEndDateRequest[accountID] !== prevProps.setLaunchOrderEndDateRequest[accountID]
    ) {
      return this.setState({
        requestTitle: 'Updating launch order end date',
        requestState: setLaunchOrderEndDateRequest[accountID],
        showLoadingIndicatorOnFetch: false,
      });
    }

    if (!requestError && setExecutedPlanRequest[accountID] !== prevProps.setExecutedPlanRequest[accountID]) {
      return this.setState({
        requestTitle: 'Executing plan',
        requestState: setExecutedPlanRequest[accountID],
        showLoadingIndicatorOnFetch: false,
      });
    }

    if (!requestError && setRejectedPlanRequest[accountID] !== prevProps.setRejectedPlanRequest[accountID]) {
      return this.setState({
        requestTitle: 'Rejecting plan',
        requestState: setRejectedPlanRequest[accountID],
        showLoadingIndicatorOnFetch: false,
      });
    }
  }

  public render() {
    const { accountID, launchLineItem, refreshSelectedPlan, setLaunchOrderEndDate, settings, scopes, absoluteMode } =
      this.props;
    const { skipEmailNotification, requestTitle, requestState, showLoadingIndicatorOnFetch } = this.state;

    const plan = this.props.processedPlansByAccountID[accountID];

    // Only display trial start & end dates in PlanReviewConfig if IASAPs are being provisioned
    const shouldDisplayTrialDates =
      plan.immediatelyActionableSingleActionProducts.length > 0 &&
      [Platform.EnterpriseTrial, Platform.StandardTrial2021].includes(plan.plannedConfig.platform);

    this.shouldLineItemSectionBeStriped = false;

    if (requestTitle && requestState && (requestState.isFetching || requestState.error)) {
      return (
        <RequestStatus title={requestTitle} requestState={requestState} showOnFetch={showLoadingIndicatorOnFetch} />
      );
    }

    return (
      <>
        <Callout title={plan.organization} className="planReview">
          <div>
            <>{plan.ldInstance} | </>
            {this.vitalLink(accountID, plan.ldInstance)}
            <div role="button" tabIndex={0} className="copy-button-container">
              <CircularIconButton
                iconName="copy-clipboard"
                intent="success"
                size="small"
                tooltip="Copy LD AccountID"
                tooltipPlacement="top"
                onPress={() => {
                  navigator.clipboard.writeText(accountID);
                  Notifications.show({
                    intent: Intent.SUCCESS,
                    message: 'Copied',
                  });
                }}
              />
            </div>
          </div>
          {/* TODO(jeremy): Enable once SFDC account ID is included in /available-plans response */}
          {/* <div> */}
          {/*  SFDC Account ID: <a href="#"></a> */}
          {/* </div> */}
          <div className="refresh-button-absolute">
            <CircularIconButton
              iconName="sync"
              intent="primary"
              size="small"
              tooltip="Refresh Plan"
              onPress={refreshSelectedPlan}
            />
          </div>
        </Callout>
        <HTMLTable className="planReview">
          <TableHeader showStartingDate shouldDisplayTrialDates={shouldDisplayTrialDates} />
          <tbody>
            <PlanReviewConfig
              title="Current Limits"
              config={plan.previousAccountConfig}
              className="currentLimits"
              displayTrialDates={shouldDisplayTrialDates}
            />
            {!absoluteMode && plan.endingLineItems && plan.endingLineItems.length > 0 && (
              <PlanReviewLineItemSection
                title="Ending"
                isSFDCLineItem
                lineItems={plan.endingLineItems}
                className="ending"
                invertQtySign
                striped={this.isLineItemSectionStriped(plan.endingLineItems)}
                setLaunchOrderEndDate={(orderID: number, endDate: Date) =>
                  setLaunchOrderEndDate(accountID, orderID, endDate, absoluteMode)
                }
                scopes={scopes}
              />
            )}
            {settings.displayImmediatelyActionableProductsInAccountProvisionReview && (
              <PlanReviewLineItemSection
                title="Immediately Actionable Products"
                isSFDCLineItem
                lineItems={plan.immediatelyActionableSingleActionProducts}
                className="starting"
                striped={this.isLineItemSectionStriped(plan.immediatelyActionableSingleActionProducts)}
                setLaunchOrderEndDate={(orderID: number, endDate: Date) =>
                  setLaunchOrderEndDate(accountID, orderID, endDate, absoluteMode)
                }
                scopes={scopes}
              />
            )}
            <PlanReviewLineItemSection
              title="Participating"
              isSFDCLineItem
              lineItems={plan.participatingLineItems}
              className="wash"
              striped={this.isLineItemSectionStriped(plan.participatingLineItems)}
              setLaunchOrderEndDate={(orderID: number, endDate: Date) =>
                setLaunchOrderEndDate(accountID, orderID, endDate, absoluteMode)
              }
              scopes={scopes}
            />
            <PlanReviewLineItemSection
              title="Starting"
              isSFDCLineItem
              lineItems={plan.startingLineItems}
              className="starting"
              striped={this.isLineItemSectionStriped(plan.startingLineItems)}
              setLaunchOrderEndDate={(orderID: number, endDate: Date) =>
                setLaunchOrderEndDate(accountID, orderID, endDate, absoluteMode)
              }
              scopes={scopes}
            />
            {!absoluteMode && plan.washLineItems && plan.washLineItems.length > 0 && (
              <PlanReviewLineItemSection
                title="Wash"
                isSFDCLineItem
                lineItems={plan.washLineItems}
                className="wash"
                striped={this.isLineItemSectionStriped(plan.washLineItems)}
                setLaunchOrderEndDate={(orderID: number, endDate: Date) =>
                  setLaunchOrderEndDate(accountID, orderID, endDate, absoluteMode)
                }
                scopes={scopes}
              />
            )}
            <PlanReviewConfig
              title="New Contracted Limits"
              config={plan.plannedConfig}
              className="newLimits"
              displayTrialDates={shouldDisplayTrialDates}
            />
            {Object.keys(plan.flagOverrides).length > 0 && (
              <tr className="flagOverrides">
                <td>Catfood {Object.keys(plan.flagOverrides).length > 1 ? 'Overrides are' : 'Override is'} Active</td>
                <td />
                <td />
                <td />
                <td />
                <td />
                <td />
                <td>
                  <PlanReviewOverrideCell value={plan.flagOverrides.seats} flag={LimitOverrideFlag.Seats} />
                </td>
                <td>
                  <PlanReviewOverrideCell value={plan.flagOverrides.hosts} flag={LimitOverrideFlag.Hosts} />
                </td>
                <td>
                  <PlanReviewOverrideCell value={plan.flagOverrides.c_mau} flag={LimitOverrideFlag.Cmau} />
                </td>
                <td>
                  <PlanReviewOverrideCell
                    value={plan.flagOverrides.experiment_events}
                    flag={LimitOverrideFlag.ExperimentEvents}
                  />
                </td>
                <td>
                  <PlanReviewOverrideCell
                    value={plan.flagOverrides.experiment_keys}
                    flag={LimitOverrideFlag.ExperimentationKeys}
                  />
                </td>
                <td>
                  <PlanReviewOverrideCell
                    value={plan.flagOverrides.experimentation_mau}
                    flag={LimitOverrideFlag.ExperimentationMau}
                  />
                </td>
                <td>
                  <PlanReviewOverrideCell
                    value={plan.flagOverrides.data_export_events}
                    flag={LimitOverrideFlag.DataExportEvents}
                  />
                </td>
                <td>
                  <PlanReviewOverrideCell value={plan.flagOverrides.traces} flag={LimitOverrideFlag.Traces} />
                </td>
                <td>
                  <PlanReviewOverrideCell value={plan.flagOverrides.sessions} flag={LimitOverrideFlag.Sessions} />
                </td>
                <td>
                  <PlanReviewOverrideCell value={plan.flagOverrides.errors} flag={LimitOverrideFlag.Errors} />
                </td>
                <td>
                  <PlanReviewOverrideCell value={plan.flagOverrides.logs} flag={LimitOverrideFlag.Logs} />
                </td>
                <td>
                  <PlanReviewOverrideCell
                    value={plan.flagOverrides.analytics_mau}
                    flag={LimitOverrideFlag.AnalyticsMau}
                  />
                </td>
                <td>
                  <PlanReviewOverrideCell
                    value={plan.flagOverrides.ai_service_connections}
                    flag={LimitOverrideFlag.AiServiceConnections}
                  />
                </td>
                <td>
                  <PlanReviewOverrideCell value={plan.flagOverrides.ai_runs} flag={LimitOverrideFlag.AIRuns} />
                </td>
                <td>
                  <PlanReviewOverrideCell value={plan.flagOverrides.vega_ai} flag={LimitOverrideFlag.VegaAI} />
                </td>
                <td>
                  <PlanReviewOverrideCell value={plan.flagOverrides.sso} flag={LimitOverrideFlag.SSO} />
                </td>
              </tr>
            )}
            {scopes.accountProvisioning && (
              <tr>
                <td colSpan={23}>
                  {scopes.optOutProvisioningEmail && (
                    <Checkbox
                      className="options"
                      onChange={this.handleSkipEmailNotificationChange}
                      checked={skipEmailNotification}
                      labelElement={<strong>Do not send provisioning email</strong>}
                    />
                  )}
                  <div className="buttonContainer">
                    <ApproveNewLimitsButton
                      plan={plan}
                      onExecuteClick={this.handleExecuteClick(
                        accountID,
                        plan.inputsHash,
                        absoluteMode,
                        this.state.skipEmailNotification,
                      )}
                    />
                    <LaunchpadButton variant="destructive" size="medium" onPress={this.handleDialogOpen}>
                      <LaunchpadIcon name="thumb-down-outline" slot="start-icon" />
                      Reject & Escalate
                    </LaunchpadButton>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </HTMLTable>
        {launchLineItem && launchLineItem.endDate && isAfter(launchLineItem.endDate, endOfYesterday()) && (
          <HTMLTable className="planReview">
            <TableHeader showStartingDate={false} shouldDisplayTrialDates={false} />
            <tbody>
              <PlanReviewLineItemSection
                title="Launch Order Exists"
                isSFDCLineItem
                lineItems={[launchLineItem]}
                className="launch"
                striped={false}
                setLaunchOrderEndDate={(orderID: number, endDate: Date) =>
                  setLaunchOrderEndDate(accountID, orderID, endDate, absoluteMode)
                }
                scopes={scopes}
              />
            </tbody>
          </HTMLTable>
        )}
        {absoluteMode && plan.washLineItems && plan.washLineItems.length > 0 && (
          <HTMLTable className="planReview">
            <TableHeader showStartingDate shouldDisplayTrialDates={false} />
            <tbody>
              <PlanReviewLineItemSection
                title="Wash"
                isSFDCLineItem
                lineItems={plan.washLineItems}
                className="wash"
                striped={this.isLineItemSectionStriped(plan.washLineItems)}
                setLaunchOrderEndDate={(orderID: number, endDate: Date) =>
                  setLaunchOrderEndDate(accountID, orderID, endDate, absoluteMode)
                }
                scopes={scopes}
              />
            </tbody>
          </HTMLTable>
        )}
        {absoluteMode && plan.endingLineItems && plan.endingLineItems.length > 0 && (
          <HTMLTable className="planReview">
            <TableHeader showStartingDate shouldDisplayTrialDates={false} />
            <tbody>
              <PlanReviewLineItemSection
                title="Recently Ended Products"
                isSFDCLineItem
                lineItems={plan.endingLineItems}
                className="wash"
                striped={this.isLineItemSectionStriped(plan.endingLineItems)}
                setLaunchOrderEndDate={(orderID: number, endDate: Date) =>
                  setLaunchOrderEndDate(accountID, orderID, endDate, absoluteMode)
                }
                scopes={scopes}
              />
            </tbody>
          </HTMLTable>
        )}
        <ModalOverlay isOpen={this.state.isOpen} onOpenChange={(open: boolean) => !open && this.handleDialogClose()}>
          <Modal>
            <Dialog>
              <Heading slot="title">
                <LaunchpadIcon name="cancel" size="small" className="icon-with-margin-right" /> Reject & Escalate Plan
              </Heading>
              <div className="plan-review-reject-dialog__content">
                <TextArea
                  className="rejectPlan"
                  fill
                  growVertically
                  placeholder="Describe the problem/reason for the rejection..."
                  onChange={this.handleCommentChange}
                />
              </div>
              <div className="plan-review-reject-dialog__actions">
                <Button text="Close" onClick={this.handleDialogClose} />
                <Button
                  text="Reject"
                  intent="primary"
                  onClick={this.handleRejectClick(accountID, plan.inputsHash, absoluteMode)}
                />
              </div>
            </Dialog>
          </Modal>
        </ModalOverlay>
      </>
    );
  }

  private vitalLink(accountID: string, ldInstance: Instance) {
    const baseUrl = vitalBaseUrl(ldInstance);
    return (
      <a href={`${baseUrl}/#/accounts/${accountID}`} target="_blank">
        {accountID}
      </a>
    );
  }

  private isLineItemSectionStriped(lineItems: LineItem[]): boolean {
    if (lineItems.length) {
      this.shouldLineItemSectionBeStriped = !this.shouldLineItemSectionBeStriped;
    }
    return this.shouldLineItemSectionBeStriped;
  }

  private handleDialogOpen = () => {
    this.setState({ isOpen: true });
  };

  private handleDialogClose = () => {
    this.setState({ isOpen: false });
  };

  private handleCommentChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    this.setState({ rejectionComment: event.target.value });
  };

  private handleExecuteClick(
    accountID: string,
    inputsHash: string,
    absoluteMode: boolean,
    skipEmailNotification: boolean,
  ) {
    return () => {
      const { userEmail } = this.props;
      this.props.setExecutedPlan(accountID, inputsHash, absoluteMode, skipEmailNotification, userEmail);
    };
  }

  private handleSkipEmailNotificationChange = () => {
    this.setState({ skipEmailNotification: !this.state.skipEmailNotification });
  };

  private handleRejectClick(accountID: string, inputsHash: string, absoluteMode: boolean) {
    return () => {
      const { userEmail } = this.props;
      this.props.setRejectedPlan(accountID, inputsHash, absoluteMode, userEmail, this.state.rejectionComment);
      this.setState({ isOpen: false, rejectionComment: '' });
    };
  }
}
